import type React from 'react';

import { useGetFace } from '@vlabs/api/luna-api/client';
import type { FaceId } from '@vlabs/api/luna-api/model';
import { DetectionIcon } from '@vlabs/icons';
import { SimilarityTag } from '@vlabs/shared/components/similarity-tag/SimilarityTag';
import ListLink from '@vlabs/shared/legacy-components/list-link/ListLink';
import { calcTagType } from '@vlabs/shared/utils';
import type { WithClassNameProp, WithDataTestid } from '@vlabs/uikit/types';
import cn from 'classnames';

import { EventFaceTopMatchSample } from '../detection-sample';
import { useTopMatchFaceInfoTranslation } from './i18n';
import st from './TopMatchFaceInfo.module.sass';
import { buildTestid } from '../../../shared/lib/utils';

interface TopMatchFaceInfoProps extends WithClassNameProp, WithDataTestid {
  faceId: FaceId;
  similarity: number;
}

const displayName = 'TopMatchFaceInfo';
const testid = buildTestid(displayName);

function TopMatchFaceInfo({
  faceId,
  similarity,
  className = undefined,
  'data-testid': dataTestId = undefined,
}: TopMatchFaceInfoProps): React.JSX.Element | null {
  const { t } = useTopMatchFaceInfoTranslation();
  const { data: face } = useGetFace(faceId, { targets: ['face_id', 'user_data', 'external_id', 'lists'] });

  if (!face) return null;

  const similarityKind = calcTagType(similarity);
  const userData = face.user_data;
  const externalId = face.external_id;

  return (
    <div
      className={cn(
        st.TopMatchFaceInfo,
        similarityKind && st[`TopMatchFaceInfo_${similarityKind}`],
        className,
      )}
      {...testid(dataTestId)}
    >
      <EventFaceTopMatchSample
        className={st.Sample}
        faceId={faceId}
        size="large"
        {...testid('Sample', dataTestId)}
      />

      <div className={st.Content}>
        <SimilarityTag
          icon={<DetectionIcon />}
          size="medium"
          title={t('similarity')}
          value={similarity}
          {...testid('SimilarityTag', dataTestId)}
        />

        <div className={st.Rows}>
          {!!userData && (
            <div className={st.Row}>
              <span className={st.RowTitle}>{t('user_data')}</span>
              <span className={st.RowValue}>{userData}</span>
            </div>
          )}
          {!!externalId && (
            <div className={st.Row}>
              <span className={st.RowTitle}>{t('external_id')}</span>
              <span className={st.RowValue}>{externalId}</span>
            </div>
          )}
          {!!face.lists?.length && (
            <div className={st.Row}>
              <span className={st.RowTitle}>{t('lists')}</span>
              <span className={st.RowValue}>
                <ListLink lists={face.lists} />
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

TopMatchFaceInfo.displayName = displayName;

export { TopMatchFaceInfo };
